"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/platform/auth/useAuth";
import { useSupplierEvaluation } from "../hooks/useSupplierEvaluation";
import {
  saveAssessmentForm,
  transitionState,
  saveSupplierDraftResponses,
} from "@/platform/firebase/firestore/supplier-evaluation";
import {
  generateAssessmentForm,
  sendSupplierInvitation,
  sendSupplierReminder,
  sendCollaboratorInvite,
} from "../api";
import { INDUSTRIES, getStepFromState, type EvaluationStep, type EvaluatorUser, type AssessmentForm } from "../types";
import { EvaluationSidebar } from "./EvaluationSidebar";
import { Step1Overview } from "./Step1Overview";
import { Step2Questionnaire } from "./Step2Questionnaire";
import { Step3SupplierResponse } from "./Step3SupplierResponse";
import { Step4Ratings } from "./Step4Ratings";
import { Step5Analysis } from "./Step5Analysis";

interface Props {
  evaluationId: string;
}

export function EvaluationDetail({ evaluationId }: Props) {
  const router = useRouter();
  const { user } = useAuth();
  const {
    project, setProject,
    assessmentForm, setAssessmentForm,
    loading, saving,
    transition, update,
    addEvaluator, removeEvaluator,
  } = useSupplierEvaluation(evaluationId);

  const [activeStep, setActiveStep] = useState<EvaluationStep | null>(null);
  const [generatingForm, setGeneratingForm]   = useState(false);
  const [sendingInvite, setSendingInvite]     = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [skippingSupplier, setSkippingSupplier] = useState(false);
  const [skippingRatings, setSkippingRatings]   = useState(false);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "400px", color: "var(--ink-mute)" }}>
        Loading…
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ padding: "24px", color: "var(--ink-mute)" }}>Evaluation not found.</div>
    );
  }

  const currentStep = getStepFromState(project.workflow_state);
  const displayStep = activeStep ?? currentStep;
  const isOwner = user?.uid === project.created_by_uid;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleGenerateForm = async () => {
    setGeneratingForm(true);
    try {
      const result = await generateAssessmentForm({
        industry_vertical: project.supplier_info.industry,
        evaluation_focus: project.evaluation_focus,
        additional_context: project.additional_context,
      });
      const form: AssessmentForm = { ...(result as unknown as AssessmentForm), scoring_summary: {} };
      await saveAssessmentForm(evaluationId, form);
      if (project.workflow_state === "created") {
        await transition("questionnaire_ready");
        setProject((p) => p ? { ...p, workflow_state: "questionnaire_ready" } : p);
        setActiveStep(2);
      }
      setAssessmentForm(form);
    } finally {
      setGeneratingForm(false);
    }
  };

  const handleSaveForm = async (form: AssessmentForm) => {
    await saveAssessmentForm(evaluationId, form);
    setAssessmentForm(form);
  };

  const handleEvaluate = async () => {
    setSendingInvite(true);
    try {
      if (project.workflow_state === "questionnaire_ready") {
        await transition("sent");
        setProject((p) => p ? { ...p, workflow_state: "sent" } : p);
      }
      setActiveStep(3);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleSendInvitation = async () => {
    setSendingInvite(true);
    try {
      const token = crypto.randomUUID();
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const portalLink = `${origin}/supplier/evaluation/${evaluationId}/${token}`;
      const industryLabel = INDUSTRIES.find((i) => i.value === project.supplier_info.industry)?.label
        ?? project.supplier_info.industry;
      await sendSupplierInvitation({
        supplier_email: project.supplier_info.email,
        contact_person: project.supplier_info.contact_person,
        company_name: project.supplier_info.company_name,
        evaluation_focus: project.evaluation_focus,
        industry: industryLabel,
        portal_link: portalLink,
        additional_context: project.additional_context,
      });
      await transition("sent", {
        supplier_token: token,
        invitation_sent_at: new Date().toISOString(),
      });
      setProject((p) => p ? { ...p, workflow_state: "sent", supplier_token: token, invitation_sent_at: new Date().toISOString() } : p);
      setActiveStep(3);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleSendReminder = async () => {
    if (!project.supplier_token) return;
    setSendingReminder(true);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const portalLink = `${origin}/supplier/evaluation/${evaluationId}/${project.supplier_token}`;
      const industryLabel = INDUSTRIES.find((i) => i.value === project.supplier_info.industry)?.label
        ?? project.supplier_info.industry;
      await sendSupplierReminder({
        supplier_email: project.supplier_info.email,
        contact_person: project.supplier_info.contact_person,
        company_name: project.supplier_info.company_name,
        evaluation_focus: project.evaluation_focus,
        industry: industryLabel,
        portal_link: portalLink,
      });
    } finally {
      setSendingReminder(false);
    }
  };

  const handleSkipSupplierResponse = async () => {
    if (!assessmentForm) return;
    setSkippingSupplier(true);
    try {
      const seedDoc = {
        sections: assessmentForm.sections.map((sec) => ({
          section_id: sec.section_id,
          section_title: sec.section_title,
          weight_percent: sec.weight_percent,
          questions: sec.questions.map((q) => ({
            question_id: q.question_id,
            question_text: q.question_text,
            supplier_score: null,
            supplier_observation: "",
            company_score: null,
            company_observation: "",
          })),
        })),
      };
      await saveSupplierDraftResponses(evaluationId, seedDoc);
      await transition("responded", { supplier_completed_at: new Date().toISOString() });
      setProject((p) => p ? { ...p, workflow_state: "responded", supplier_completed_at: new Date().toISOString() } : p);
      setActiveStep(4);
    } finally {
      setSkippingSupplier(false);
    }
  };

  const handleSkipRatings = async () => {
    setSkippingRatings(true);
    try {
      await transition("scored", { auditor_scored_at: new Date().toISOString() });
      setProject((p) => p ? { ...p, workflow_state: "scored", auditor_scored_at: new Date().toISOString() } : p);
      setActiveStep(5);
    } finally {
      setSkippingRatings(false);
    }
  };

  const handleAddEvaluator = async (email: string) => {
    if (!user) return;
    const evaluator: EvaluatorUser = { uid: email, email, added_at: new Date().toISOString() };
    await addEvaluator(evaluator);
    // Also send invite email (best-effort)
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    sendCollaboratorInvite({
      collaborator_email: email,
      collaborator_name: email,
      company_name: project.supplier_info.company_name,
      project_name: project.project_name,
      portal_link: `${origin}/agentic-workflows/supplier-evaluation/${evaluationId}`,
    }).catch(console.error);
  };

  const handleRemoveEvaluator = async (ev: EvaluatorUser) => {
    await removeEvaluator(ev);
  };

  // ── Step renderer ──────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (displayStep) {
      case 1:
        return (
          <Step1Overview
            project={project}
            onNext={() => setActiveStep(2)}
            onProjectUpdate={(p) => setProject(p)}
            isOwner={isOwner}
            onAddEvaluator={handleAddEvaluator}
            onRemoveEvaluator={handleRemoveEvaluator}
          />
        );
      case 2:
        return (
          <Step2Questionnaire
            project={project}
            assessmentForm={assessmentForm}
            generating={generatingForm}
            onGenerate={handleGenerateForm}
            onSaveForm={handleSaveForm}
            onEvaluate={handleEvaluate}
            evaluating={sendingInvite}
          />
        );
      case 3:
        return (
          <Step3SupplierResponse
            project={project}
            evaluationId={evaluationId}
            onSendToSupplier={handleSendInvitation}
            onSendReminder={handleSendReminder}
            sendingInvite={sendingInvite}
            sendingReminder={sendingReminder}
            onSkip={handleSkipSupplierResponse}
            skipping={skippingSupplier}
            onSkipRatings={handleSkipRatings}
            skippingRatings={skippingRatings}
            onProjectUpdate={(p) => setProject(p)}
            onGoToScoring={() => router.push(`/agentic-workflows/supplier-evaluation/${evaluationId}/score`)}
          />
        );
      case 4:
        return (
          <Step4Ratings
            project={project}
            assessmentForm={assessmentForm}
            onStartScoring={() => router.push(`/agentic-workflows/supplier-evaluation/${evaluationId}/score`)}
            onAnalyze={() => router.push(`/agentic-workflows/supplier-evaluation/${evaluationId}/analysis`)}
          />
        );
      case 5:
        return <Step5Analysis evaluationId={evaluationId} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ display: "flex", height: "calc(100vh - 60px)", overflow: "hidden" }}>
      <EvaluationSidebar
        project={project}
        activeStep={displayStep}
        onStepClick={setActiveStep}
        onBack={() => router.push("/agentic-workflows/supplier-evaluation")}
      />

      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto", padding: "24px" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
            <div>
              <h1 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 2px" }}>{project.project_name}</h1>
              <p style={{ fontSize: "13px", color: "var(--ink-mute)", margin: 0 }}>{project.supplier_info.company_name}</p>
            </div>
            <span style={{
              fontSize: "12px", padding: "4px 12px", borderRadius: "100px",
              background: "var(--paper-3)", border: "1px solid var(--line-strong)",
              color: "var(--ink-soft)",
            }}>
              {project.workflow_state.replace("_", " ")}
            </span>
          </div>

          {renderStep()}
        </div>
      </div>
    </div>
  );
}
